import React, { createContext, useContext } from 'react';

/*
 	This context provides a utility function, routeHandler(), to help you handle routing in the application
	and avoid patterns where you would have to pass down a prop through multiple levels
	of components in order to apply routing to a link.

	Per default, the Link atom component utilizes RouteContext, making it possible for you to route a
	link without having to manually pass a click handler to it.
	To override this behavior, simply pass the 'disableRouteHandler' prop to the Link component.

	The routeHandler utility function can also be utilized in other use cases where you might want to
	handle routing but don't want to use the Link component.

	To use the routeHandler, import useRouteContext and call it like this:
	const routeHandler = useRouteContext();
	<SomeComponent onClick={(e) => routeHandler(e, '/some/path')} />
*/

type RouteHandler = (event: React.MouseEvent | React.ChangeEvent<HTMLSelectElement>, url: string, locale?: string | boolean, target?: string) => void;

interface RouteContextProviderProps {
	children?: React.ReactNode;
	router: {
		push: (url: string, as?: string, options?: { locale: string | boolean }) => void;
	};
}

export const RouteContext = createContext(null);

export const RouteContextProvider = (props: RouteContextProviderProps) => {
	const { children, router } = props;

	const routeHandler: RouteHandler = (event, url, locale, target) => {
		if (!router) return;

		const { ctrlKey, shiftKey } = event as React.MouseEvent;
		const hasTargetNewWindow = ctrlKey || shiftKey || target === '_blank';

		// Use default browser behavior if the link has target="_blank" or if the user is holding down the ctrl or shift key.
		if (hasTargetNewWindow) return;

		event?.preventDefault();

		const isInternalLink = url.startsWith('/') || url.startsWith('#') || url.startsWith('?') || url.startsWith(window.origin);

		// Reroute if internal link. Otherwise just redirect.
		// As stated in the Next.js docs (https://nextjs.org/docs/api-reference/next/router):
		// "You don't need to use router.push for external URLs. window.location is better suited for those cases."

		if (!isInternalLink) {
			window.location.href = url;
			return;
		}

		if (locale) {
			router.push('/', '/', { locale: locale });
			return;
		}

		router.push(url, null, { locale: locale });
	};

	return <RouteContext.Provider value={{ routeHandler, router }}>{children}</RouteContext.Provider>;
};

/**
 * @function useRouteContext
 * @returns {RouteHandler} routeHandler(event, href)
 */
export const useRouteContext = (): { routeHandler: RouteHandler; router } => {
	return useContext(RouteContext);
};
